import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import SummarizationLoaderComponent from 'components/ai/SummarizationLoaderComponent';
import JobbableAttachmentJobSubscriber from 'components/shared/ai/summary/JobbableAttachmentJobSubscriber';
import TimingExplainer from 'components/shared/ai/TimingExplainer';
import useAIJobChannel from 'hooks/useAIJobChannel';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import { useStageTimer } from 'hooks/useStageTimer';
import { updateJobbableCacheWithAIJob } from 'lib/aiJobHelpers';
import Summarization from 'models/Summarization';
import { summarizationQueryKeys } from 'services/api/summarizations';

enum Stage {
  ProcessingFiles = 'Processing files',
  IdentifyingPatientDemographics = 'Identifying patient demographics',
  GeneratingSummary = 'Generating summary',
  FinishingUp = 'Finishing up',
}

const STAGES = Array.from(Object.values(Stage));

type SummaryLoadingStateProps = {
  summarization: Summarization;
  onLoadingFinished: () => void;
};

export default function LoadingState({ summarization, onLoadingFinished }: SummaryLoadingStateProps) {
  const invalidateQueries = useInvalidateQueries();

  const { attachments, patientDemographicsJob, summaryJob } = summarization;

  const fileCount = attachments.length;
  const summaryJobUpdatedAt = summaryJob?.updatedAt;

  // Only start the timer when the summary job is awaiting (file processing + patient demographics are done)
  const timerReferenceDate = summaryJob?.isAwaiting ? summaryJobUpdatedAt : null;
  const onFinalStage = useStageTimer(timerReferenceDate, 60);

  const stage = useMemo(() => {
    if (summaryJob?.isCompleted) {
      return STAGES.length + 1;
    }

    let currentStage: Stage;

    if (summaryJob?.isAwaiting) {
      if (onFinalStage) {
        currentStage = Stage.FinishingUp;
      } else {
        currentStage = Stage.GeneratingSummary;
      }
    } else if (patientDemographicsJob?.isAwaiting) {
      currentStage = Stage.IdentifyingPatientDemographics;
    } else if (patientDemographicsJob?.isPending) {
      currentStage = Stage.ProcessingFiles;
    } else {
      return 0;
    }

    return STAGES.indexOf(currentStage) + 1;
  }, [patientDemographicsJob, summaryJob, onFinalStage]);

  const [currentStage, setCurrentStage] = useState(stage);

  useEffect(() => {
    setCurrentStage(stage);
  }, [stage]);

  const count = useMemo(() => {
    return currentStage
      ? attachments.filter((attachment) => attachment.extractDataAIJob && !attachment.extractDataAIJob.isProcessing)
          .length
      : 0;
  }, [currentStage, attachments]);

  const [completedCount, setCompletedCount] = useState(count);

  useEffect(() => {
    if (currentStage) {
      setTimeout(() => {
        setCompletedCount(count);
      }, 500);
    }
  }, [currentStage, count]);

  const stages = useMemo(
    () =>
      STAGES.map((stage) => ({
        title: stage,
        ...(stage === Stage.ProcessingFiles
          ? {
              documentStages: [
                { title: 'Scanning document text', count: completedCount },
                { title: 'Extracting key information', count: completedCount },
                { title: 'Creating source citations', count: completedCount },
              ],
            }
          : {}),
      })),
    [completedCount]
  );

  // subscribe to Patient Demographics job without any notification tracking
  useAIJobChannel({
    aiJobId: patientDemographicsJob?.isProcessing ? patientDemographicsJob.id : undefined,
    onMessageReceived: (updatedJob) => {
      if (updatedJob.isPending) return;

      if (updatedJob.isCompleted) {
        invalidateQueries({ summarization: { id: summarization?.id } });
      } else {
        updateJobbableCacheWithAIJob(summarizationQueryKeys.show({ id: summarization?.id }), updatedJob);
      }
    },
  });

  return (
    <LoaderContainer>
      {attachments.map((attachment) => (
        <JobbableAttachmentJobSubscriber key={attachment.id} attachment={attachment} jobbable={summarization} />
      ))}
      <SummarizationLoaderComponent
        type='summary'
        fileCount={fileCount}
        stages={stages}
        stage={currentStage}
        onLoadingFinished={onLoadingFinished}
      />
      <TimingExplainer jobType='summary' />
    </LoaderContainer>
  );
}

const LoaderContainer = styled.div`
  margin: 48px 0 268px 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & > *:last-child {
    position: fixed;
    width: unset;
  }
`;
